import { useState, useCallback, useEffect, useRef } from 'react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { listHospitals } from '../services/hospitalService';
import {
  createExportJob,
  getExportJobStatus,
  getExportDownloadUrl,
  type ExportJobStatus,
} from '../services/exportService';
import type { Hospital } from '../types';

const AVAILABLE_FIELDS = [
  { value: 'appt_id', label: 'Appointment ID' },
  { value: 'hospital_name', label: 'Hospital Name' },
  { value: 'hospital_code', label: 'Hospital Code' },
  { value: 'calculated_at', label: 'Calculated At' },
  { value: 'validation_status', label: 'Validation Status' },
  { value: 'hospital_share', label: 'Hospital Share' },
  { value: 'ar_ap', label: 'AR/AP' },
  { value: 'execution_time_ms', label: 'Execution Time (ms)' },
];

export default function ExportPage() {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(true);

  const [hospitalUuid, setHospitalUuid] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');
  const [format, setFormat] = useState<'CSV' | 'EXCEL'>('CSV');
  const [selectedFields, setSelectedFields] = useState<string[]>(
    AVAILABLE_FIELDS.map((f) => f.value)
  );

  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState<ExportJobStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    listHospitals({ limit: 1000, active: 'true' })
      .then((res) => setHospitals(res.hospitals))
      .catch(() => {})
      .finally(() => setIsLoadingHospitals(false));
  }, []);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = useCallback((id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getExportJobStatus(id);
        setJobStatus(status);
        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        // keep polling on transient errors
      }
    }, 2000);
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!dateFrom || !dateTill) return;
    setIsCreating(true);
    setCreateError('');
    setJobId('');
    setJobStatus(null);

    try {
      const res = await createExportJob({
        hospitalUuid: hospitalUuid || undefined,
        dateFrom,
        dateTill,
        format,
        includeFields: selectedFields.length < AVAILABLE_FIELDS.length ? selectedFields : undefined,
      });
      setJobId(res.jobId);
      startPolling(res.jobId);
    } catch (err: any) {
      setCreateError(
        err?.response?.data?.error || err?.message || 'Failed to create export job.'
      );
    } finally {
      setIsCreating(false);
    }
  }, [hospitalUuid, dateFrom, dateTill, format, selectedFields, startPolling]);

  const toggleField = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const toggleAllFields = () => {
    if (selectedFields.length === AVAILABLE_FIELDS.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(AVAILABLE_FIELDS.map((f) => f.value));
    }
  };

  const handleDownload = async () => {
    if (!jobId) return;
    try {
      const response = await fetch(
        getExportDownloadUrl(jobId),
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` },
          mode: 'cors',
        }
      );
      if (!response.ok) throw new Error('Download failed');
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition?.match(/filename="?(.+?)"?$/);
      const fileName = match?.[1] || `export-${jobId}.${format === 'CSV' ? 'csv' : 'xlsx'}`;
      const blob = await response.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      setCreateError('Failed to download file.');
    }
  };

  const isRunning = jobStatus?.status === 'PENDING' || jobStatus?.status === 'IN_PROGRESS';
  const isComplete = jobStatus?.status === 'COMPLETED';
  const isFailed = jobStatus?.status === 'FAILED';

  if (isLoadingHospitals) {
    return (
      <div className="export-page">
        <LoadingSpinner message="Loading hospitals..." />
      </div>
    );
  }

  return (
    <div className="export-page">
      <div className="page-header">
        <h2>Export Calculations</h2>
      </div>

      <section className="export-section">
        <div className="export-form">
          <Select
            label="Hospital (optional)"
            options={[
              { value: '', label: 'All Hospitals' },
              ...hospitals.map((h) => ({
                value: h.hospitalUuid,
                label: `${h.hospitalName} (${h.hospitalCode})`,
              })),
            ]}
            value={hospitalUuid}
            onChange={(e) => setHospitalUuid(e.target.value)}
            disabled={isRunning}
          />
          <Input
            label="Date From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            disabled={isRunning}
          />
          <Input
            label="Date Till"
            type="date"
            value={dateTill}
            onChange={(e) => setDateTill(e.target.value)}
            disabled={isRunning}
          />
          <Select
            label="Format"
            options={[
              { value: 'CSV', label: 'CSV' },
              { value: 'EXCEL', label: 'Excel' },
            ]}
            value={format}
            onChange={(e) => setFormat(e.target.value as 'CSV' | 'EXCEL')}
            disabled={isRunning}
          />
        </div>

        <fieldset className="export-fields" disabled={isRunning}>
          <legend>Fields to Include</legend>
          <label className="export-field-toggle">
            <input
              type="checkbox"
              checked={selectedFields.length === AVAILABLE_FIELDS.length}
              onChange={toggleAllFields}
            />
            Select All
          </label>
          <div className="export-field-list">
            {AVAILABLE_FIELDS.map((f) => (
              <label key={f.value} className="export-field-toggle">
                <input
                  type="checkbox"
                  checked={selectedFields.includes(f.value)}
                  onChange={() => toggleField(f.value)}
                />
                {f.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="export-actions">
          <Button
            size="lg"
            onClick={handleGenerate}
            isLoading={isCreating}
            disabled={!dateFrom || !dateTill || selectedFields.length === 0 || isCreating || isRunning}
          >
            Generate Export
          </Button>
        </div>
        {createError && <Alert type="error">{createError}</Alert>}
      </section>

      {jobId && (
        <section className="export-section" aria-label="Export job status">
          <h3>Job: {jobId}</h3>

          {isRunning && (
            <LoadingSpinner size="sm" message="Generating export file..." />
          )}

          {isComplete && jobStatus && (
            <div className="export-complete">
              <Alert type="success">Export completed.</Alert>
              <div className="export-download">
                <Button onClick={handleDownload}>Download File</Button>
                {jobStatus.expiresAt && (
                  <p className="export-expiry">
                    Download available until{' '}
                    {new Date(jobStatus.expiresAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}

          {isFailed && (
            <Alert type="error">
              Export failed: {jobStatus?.error || 'Unknown error'}
            </Alert>
          )}
        </section>
      )}
    </div>
  );
}
